/**
 * Deterministic Radix Dialog → Razorpay Checkout handover helpers.
 *
 * The intermittent Android Chrome UPI Intent bug on this app came from
 * a race between Radix Dialog's async unmount (React commit + aria-hidden
 * release + CSS close animation) and Razorpay's `rzp.open()` mounting a
 * new iframe on top. If the Radix Dialog was still "open" (aria-hidden
 * on outside content / focus-trapped) when Razorpay's iframe requested
 * a top-level `intent://` navigation, Chrome dropped the navigation
 * because the user-activation-carrying focus was pinned inside our
 * Radix Dialog.
 *
 * This module gives a signal-driven, not time-driven, handover so the
 * flow is deterministic across devices, network speeds, and GC pauses.
 */

/**
 * Poll the DOM (via requestAnimationFrame) until every Radix Dialog
 * side-effect is fully released. Resolves immediately once *both*:
 *   • no element with `[role="dialog"][data-state="open"]` remains
 *   • `document.body` has no `aria-hidden` attribute
 *
 * A hard safety cap of `maxFrames` (~500 ms at 60 fps) ensures we never
 * hang forever if a third-party overlay pins those attributes.
 */
export function waitForRadixReleased(maxFrames = 30) {
  return new Promise((resolve) => {
    let frames = 0;
    const tick = () => {
      const stillOpen = document.querySelector('[role="dialog"][data-state="open"]');
      const bodyHidden = document.body.getAttribute("aria-hidden");
      if (!stillOpen && !bodyHidden) return resolve();
      if (++frames >= maxFrames) return resolve();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}
